import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { timelineApi } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertTriangle,
  Clock,
  CheckCircle2,
  MessageSquare,
  FileText,
  FolderKanban,
  ArrowRight,
  Timer,
  AlertCircle,
  Send,
  RefreshCw,
  Eye,
  Download,
  Upload,
  Hash,
  Calendar,
  ListChecks,
  Link as LinkIcon,
  Mail,
  AlignLeft,
  ChevronRight,
  ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import FileUploadField from "../components/FileUploadField";
import TaskTimeline, { TimelineProgressBar } from "../components/TaskTimeline";
import SetupChatButton from "../components/SetupChatButton";
import TaskStageBadge from "../components/TaskStageBadge";
import { isTaskDone } from "../lib/taskStage";
import ProjectPassport from "../components/client-portal/ProjectPassport";
import ProjectPayments from "../components/client-portal/ProjectPayments";
import CabinetDashboard from "../components/client-portal/CabinetDashboard";
import { downloadClientDocument, PenaltyPanel, penaltyRowsFromPayments } from "../components/client-portal/PaymentBits";
import {
  fmtDate, money, moneyList, sumByCurrency, plural, days, PROJECT_STATUS_UK, isProjectDone,
} from "../components/client-portal/format";
import { tr } from "../components/client-portal/i18n";

const ClientPortalPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Read-only "view as client" mirror: an internal user opens this page with
  // ?as_client=<id> to see EXACTLY what that client sees. All write actions are
  // blocked; a banner makes the mode obvious. Empty for real clients.
  const previewClientId = useMemo(
    () => new URLSearchParams(window.location.search).get("as_client"),
    [],
  );
  const isPreview = !!previewClientId;
  const [portalData, setPortalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [showDoneProjects, setShowDoneProjects] = useState(false);
  const [showAllApprovals, setShowAllApprovals] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [responseComment, setResponseComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [dialogMode, setDialogMode] = useState("preview"); // "preview" or "fill"
  const [templateFieldValues, setTemplateFieldValues] = useState({});
  const [taskTimelines, setTaskTimelines] = useState({}); // {task_id: timelineProgress}
  const [viewHistoryRequest, setViewHistoryRequest] = useState(null); // For viewing responded requests
  const [viewHistoryLoading, setViewHistoryLoading] = useState(false);
  
  // Task review state
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskReviewFeedback, setTaskReviewFeedback] = useState("");
  const [reviewingTask, setReviewingTask] = useState(false);
  
  // Task change request state
  const [selectedChangeRequest, setSelectedChangeRequest] = useState(null);
  const [changeRequestComment, setChangeRequestComment] = useState("");
  const [submittingChangeRequest, setSubmittingChangeRequest] = useState(false);

  // Field type icons for template preview
  const FIELD_ICONS = {
    checkbox: CheckCircle2,
    text: FileText,
    number: Hash,
    dropdown: ListChecks,
    file: Upload,
    date: Calendar,
    textarea: AlignLeft,
    url: LinkIcon,
    email: Mail,
  };

  useEffect(() => {
    fetchPortalData();
    // Refresh every 10 seconds to update penalty timers
    const interval = setInterval(fetchPortalData, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchPortalData = async () => {
    try {
      const res = await api.get(
        previewClientId
          ? `/client/portal?as_client=${encodeURIComponent(previewClientId)}`
          : "/client/portal",
      );
      setPortalData(res.data);

      // Load timeline progress for all tasks in all projects
      const allTasks = [];
      (res.data?.projects || []).forEach(project => {
        (project.tasks || []).forEach(task => {
          allTasks.push(task);
        });
      });
      
      if (allTasks.length > 0) {
        const timelinesMap = {};
        await Promise.all(
          allTasks.map(async (task) => {
            try {
              const timelineRes = await timelineApi.getTaskTimeline(task.id);
              if (timelineRes.data) {
                timelinesMap[task.id] = timelineRes.data;
              }
            } catch (e) {
              // Task may not have timeline
            }
          })
        );
        setTaskTimelines(timelinesMap);
      }
    } catch (error) {
      console.error("Error fetching portal data:", error);
      toast.error(tr("Не вдалося завантажити кабінет"));
    } finally {
      setLoading(false);
    }
  };

  // Task review functions
  const handleTaskReview = async (taskId, action) => {
    if (isPreview) return toast.info(tr("Режим перегляду — дія недоступна"));
    setReviewingTask(true);
    try {
      await api.post(`/client/tasks/${taskId}/review`, {
        action,
        feedback: taskReviewFeedback,
      });
      toast.success(action === "approve" ? tr("Роботу погоджено!") : tr("Запит на правки надіслано"));
      setSelectedTask(null);
      setTaskReviewFeedback("");
      fetchPortalData();
    } catch (error) {
      console.error("Error reviewing task:", error);
      toast.error(tr("Не вдалося надіслати відгук"));
    } finally {
      setReviewingTask(false);
    }
  };

  const handleRespond = async (requestId, responseType) => {
    if (isPreview) return toast.info(tr("Режим перегляду — дія недоступна"));
    // Validate fields before submitting
    const { valid, errors } = validateFields();
    if (!valid) {
      errors.forEach(err => toast.error(err));
      return;
    }
    
    setSubmitting(true);
    try {
      await api.put(`/client-requests/${requestId}/respond`, {
        response_type: responseType,
        comment: responseComment,
        files: [],
        template_data: Object.keys(templateFieldValues).length > 0 ? templateFieldValues : null,
      });
      toast.success(
        responseType === "approved"
          ? tr("Роботу погоджено! Нарахування пені зупинено.")
          : responseType === "revision"
          ? tr("Запит на правки надіслано")
          : tr("Інформацію надано")
      );
      setSelectedRequest(null);
      setResponseComment("");
      setTemplateFieldValues({});
      setDialogMode("preview");
      fetchPortalData();
    } catch (error) {
      toast.error(tr("Не вдалося надіслати відповідь"));
    } finally {
      setSubmitting(false);
    }
  };

  const openRequestDialog = (req) => {
    setSelectedRequest(req);
    // Always show fill mode directly, no preview step needed
    setDialogMode("fill");
    setResponseComment("");
    setTemplateFieldValues({});
  };

  const closeRequestDialog = () => {
    setSelectedRequest(null);
    setResponseComment("");
    setTemplateFieldValues({});
    setDialogMode("preview");
  };

  const openHistoryRequestDetail = async (requestId) => {
    setViewHistoryLoading(true);
    try {
      const res = await api.get(`/client-requests/${requestId}`);
      setViewHistoryRequest(res.data);
    } catch (error) {
      toast.error(tr("Не вдалося завантажити деталі запиту"));
    } finally {
      setViewHistoryLoading(false);
    }
  };

  const updateFieldValue = (fieldId, value) => {
    setTemplateFieldValues(prev => ({ ...prev, [fieldId]: value }));
  };

  const isValidUrl = (string) => {
    if (!string) return true; // Empty is handled by required validation
    try {
      // Allow with or without protocol
      const urlToTest = string.startsWith("http") ? string : `https://${string}`;
      new URL(urlToTest);
      // Basic pattern check for domain
      return /^(https?:\/\/)?[\w.-]+\.[a-z]{2,}(\/.*)?$/i.test(string);
    } catch {
      return false;
    }
  };

  const isValidEmail = (email) => {
    if (!email) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validateFields = () => {
    if (!selectedRequest?.template_info?.fields) return { valid: true, errors: [] };
    
    const errors = [];
    const fields = selectedRequest.template_info.fields;
    
    for (const field of fields) {
      const value = templateFieldValues[field.id];
      const hasValue = value && (typeof value === "string" ? value.trim() : true);
      
      // Check required
      if (field.is_required && !hasValue) {
        errors.push(`${field.label} is required`);
        continue;
      }
      
      // Type-specific validation
      if (hasValue) {
        if (field.field_type === "url" && !isValidUrl(value)) {
          errors.push(`${field.label} must be a valid URL (e.g., example.com or https://example.com)`);
        }
        if (field.field_type === "email" && !isValidEmail(value)) {
          errors.push(`${field.label} must be a valid email address`);
        }
      }
    }
    
    return { valid: errors.length === 0, errors };
  };

  const validateRequiredFields = () => {
    const { valid } = validateFields();
    return valid;
  };

  const formatTimeRemaining = (hours) => {
    if (hours <= 0) return "OVERDUE";
    if (hours < 1) return `${Math.round(hours * 60)} min`;
    if (hours < 24) return `${Math.round(hours)} hrs`;
    const days = Math.floor(hours / 24);
    const remainingHours = Math.round(hours % 24);
    return `${days}d ${remainingHours}h`;
  };

  const formatTimeElapsed = (hours) => {
    if (hours < 1) return `${Math.round(hours * 60)} min ago`;
    if (hours < 24) return `${Math.round(hours)} hrs ago`;
    const days = Math.floor(hours / 24);
    return `${days} days ago`;
  };

  const getRequestTypeLabel = (type) => {
    switch (type) {
      case "info_request":
        return tr("Потрібна інформація");
      case "work_review":
        return tr("Перегляд роботи");
      case "approval_request":
        return tr("Потрібне погодження");
      default:
        return type;
    }
  };

  const getRequestTypeIcon = (type) => {
    switch (type) {
      case "info_request":
        return <FileText className="h-5 w-5" />;
      case "work_review":
        return <Eye className="h-5 w-5" />;
      case "approval_request":
        return <CheckCircle2 className="h-5 w-5" />;
      case "scope_approval":
        return <AlertTriangle className="h-5 w-5" />;
      default:
        return <MessageSquare className="h-5 w-5" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  const { action_required, action_required_count, overdue_count, projects, history, tasks_awaiting_approval, task_change_requests, payments, setup_tasks, client_contacts } =
    portalData || {};
  
  // Download invoice or act (PDF) — shared with /client/payments.
  const handleDownloadDocument = (paymentId, type) => downloadClientDocument(paymentId, type, "uk");
  
  // Handle task change request approval/rejection
  const handleChangeRequestResponse = async (requestId, action) => {
    if (isPreview) return toast.info(tr("Режим перегляду — дія недоступна"));
    setSubmittingChangeRequest(true);
    try {
      await api.put(`/client/task-change-requests/${requestId}`, {
        action: action,
        comment: changeRequestComment
      });
      toast.success(action === "approve" ? tr("Зміни схвалено!") : tr("Зміни відхилено"));
      setSelectedChangeRequest(null);
      setChangeRequestComment("");
      fetchPortalData();
    } catch (error) {
      console.error("Error responding to change request:", error);
      toast.error(tr("Не вдалося обробити запит"));
    } finally {
      setSubmittingChangeRequest(false);
    }
  };
  
  // Format change type for display
  const getChangeTypeLabel = (type) => {
    switch (type) {
      case "budget_change": return tr("Зміна бюджету");
      case "deadline_change": return tr("Зміна дедлайну");
      case "scope_change": return tr("Зміна обсягу робіт");
      default: return type;
    }
  };
  
  const getChangeTypeIcon = (type) => {
    switch (type) {
      case "budget_change": return <span className="text-green-400">$</span>;
      case "deadline_change": return <Calendar className="h-4 w-4 text-blue-400" />;
      case "scope_change": return <FileText className="h-4 w-4 text-purple-400" />;
      default: return <AlertCircle className="h-4 w-4" />;
    }
  };

  // Filter overdue requests
  // Overdue PAYMENT demands live in the Payments block (+ /client/payments) —
  // drop payment_request from the requests list to kill the duplicate.
  // Non-payment requests (content/files/etc.) still surface there.
  const overdueRequests =
    action_required?.filter(req => req.is_overdue && req.request_type !== "payment_request") || [];
  const pendingRequests = action_required?.filter(req => !req.is_overdue) || [];
  
  // Calculate total penalty
  const totalPenalty = action_required?.reduce((sum, req) => sum + (req.penalty_amount || 0), 0) || 0;

  // Tasks awaiting approval - split by overdue
  const overdueApprovals = tasks_awaiting_approval?.filter(t => t.is_overdue) || [];
  const pendingApprovals = tasks_awaiting_approval?.filter(t => !t.is_overdue) || [];

  // ── Overview: selected project + plain-language status strip ───────────────
  // Company and person are separate: greet the person by name (never by a
  // chunk of the company name), show the company on its own line.
  const companyName =
    portalData?.company_name || portalData?.viewing_as?.client_name || "";
  // null from the backend means "no real person" — must NOT fall back to
  // user.name (for company-named accounts that is the company again).
  const viewerName =
    portalData && "viewer_name" in portalData
      ? portalData.viewer_name || ""
      : isPreview ? "" : user?.name || "";

  const isDoneTask = isTaskDone;
  // Active first (nearest deadline first), finished ones folded behind a toggle.
  const byDeadline = (a, b) => (a.deadline || "9999").localeCompare(b.deadline || "9999");
  const activeProjects = (projects || []).filter(p => !isProjectDone(p)).sort(byDeadline);
  const doneProjects = (projects || []).filter(isProjectDone)
    .sort((a, b) => (b.deadline || "").localeCompare(a.deadline || ""));
  const selectedProject =
    (projects || []).find(p => p.id === selectedProjectId) ||
    activeProjects.find(p => p.tasks?.length > 0) ||
    activeProjects[0] ||
    doneProjects[0] ||
    null;
  const selectedTasks = selectedProject?.tasks || [];
  const selectedDone = selectedTasks.filter(isDoneTask).length;
  const selectedPayments = (payments || []).filter(p => p.project_id === selectedProject?.id);

  // Everything on the dashboard follows the SELECTED project (the strip used to
  // count all projects while the passport below showed one — it didn't change
  // on switching). Other projects' debt shows as dots in the project picker.
  // Only actually-invoiced amounts count as "to pay"; currencies never mixed.
  const inProject = (x) => x.project_id === selectedProject?.id;
  const overdueDue = sumByCurrency(selectedPayments.filter(p => p.state === "overdue"));
  const invoicedDue = sumByCurrency(selectedPayments.filter(p => p.state === "invoiced"));
  const nextDueDate = selectedPayments
    .filter(p => p.state === "invoiced" && p.invoice_due_date)
    .map(p => p.invoice_due_date)
    .sort()[0];
  const upcomingCount = selectedPayments.filter(p => p.state === "not_invoiced").length;
  const penaltySum = selectedPayments.reduce((a, p) => a + (p.penalty?.amount || 0), 0);
  const penaltyRules = Object.fromEntries((projects || []).map((p) => [p.id, p.penalty_rule]));

  // "Needs you": actions that block the client. Payment demands are EXCLUDED —
  // money is its own tile, counting them here would double-count.
  const nonPaymentPending = pendingRequests.filter(
    r => r.request_type !== "payment_request" && inProject(r),
  );
  const projOverdueRequests = overdueRequests.filter(inProject);
  const projApprovals = (tasks_awaiting_approval || []).filter(inProject);
  const projChanges = (task_change_requests || []).filter(inProject);
  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // "Від вас" = EVERYTHING that waits on the client: an invoiced-but-unpaid
  // payment (overdue first) is an action too — not only our requests and works
  // to approve. Showing "Нічого не потрібно" next to an overdue bill was wrong.
  const duePayments = selectedPayments
    .filter(p => p.state === "overdue" || p.state === "invoiced")
    .sort((a, b) => (a.state === "overdue" ? 0 : 1) - (b.state === "overdue" ? 0 : 1)
      || (a.invoice_due_date || "").localeCompare(b.invoice_due_date || ""));
  const goToPayment = (p) => navigate(`/client/payments?project=${p.project_id}`);
  const approvalsCount = projApprovals.length + projChanges.length;
  const requestsCount = projOverdueRequests.length + nonPaymentPending.length;
  const needsYouCount = duePayments.length + requestsCount + approvalsCount;
  // Each action = short title (fits the tile) + detail line underneath.
  const topActions = [
    ...duePayments.map(p => ({
      title: `${tr("Оплатити")} ${money(p.amount, p.currency)}`,
      sub: `${p.state === "overdue" ? `${tr("Прострочено")} · ` : ""}${p.base_name || p.name}${p.part_total > 1 ? ` (${p.part_index}/${p.part_total})` : ""}`,
      urgent: p.state === "overdue",
      go: () => goToPayment(p),
    })),
    ...projOverdueRequests.map(r => ({ title: tr("Відповісти на запит"), sub: `${tr("Прострочено")} · ${r.title}`, urgent: true, go: () => navigate("/client/requests?seg=info") })),
    ...projApprovals.map(t => ({ title: tr("Погодити роботу"), sub: t.task_name, go: () => navigate("/client/requests?seg=approve") })),
    ...nonPaymentPending.map(r => ({ title: tr("Відповісти на запит"), sub: r.title, go: () => navigate("/client/requests?seg=info") })),
    ...projChanges.map(r => ({
      title: tr("Погодити зміни"),
      sub: r.task_name || tr("зміни в задачі"),
      go: () => navigate("/client/requests?seg=changes"),
    })),
  ];
  const needsYouBreakdown = [
    duePayments.length && `${duePayments.length} ${plural(duePayments.length, ["рахунок", "рахунки", "рахунків"])} ${tr("до оплати")}`,
    requestsCount && `${requestsCount} ${plural(requestsCount, ["запит", "запити", "запитів"])}`,
    approvalsCount && `${approvalsCount} ${tr("на погодження")}`,
  ].filter(Boolean).join(" · ");

  return (
    <div data-testid="client-portal-page">
      {/* Read-only "view as client" banner (internal user mirroring a client). */}
      {isPreview && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, margin: "16px clamp(16px,3vw,40px) 0", padding: "10px 16px", borderRadius: 10, border: "1px solid rgba(217,119,6,.5)", background: "rgba(120,53,15,.3)", color: "#fde68a", fontSize: 13 }}>
          <span>{tr("Перегляд кабінету клієнта")}{portalData?.viewing_as?.client_name ? `: ${portalData.viewing_as.client_name}` : ""} · {tr("режим лише для перегляду")}</span>
          <button type="button" onClick={() => window.close()} style={{ textDecoration: "underline" }}>{tr("Закрити")}</button>
        </div>
      )}

      {/* Overview — 1:1 with the approved prototype (CabinetDashboard). */}
      <CabinetDashboard
        portal={portalData}
        taskTimelines={taskTimelines}
        viewerName={viewerName}
        companyName={companyName}
        preview={isPreview}
      />

      {/* Response Dialog - with Preview Mode */}
      <Dialog open={!!selectedRequest} onOpenChange={closeRequestDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              {selectedRequest && getRequestTypeIcon(selectedRequest.request_type)}
              {selectedRequest?.title}
            </DialogTitle>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-4">
              {/* Timer Warning */}
              {selectedRequest.is_overdue && (
                <div className="p-3 bg-red-900/30 border border-red-500/30 rounded-lg">
                  <div className="flex items-center gap-2 text-red-400">
                    <AlertCircle className="h-5 w-5" />
                    <span className="font-semibold">{tr("Цей запит прострочено!")}</span>
                  </div>
                </div>
              )}

              {/* PREVIEW MODE - Shows what data is required */}
              {dialogMode === "preview" && selectedRequest.template_info && (
                <>
                  {/* Template Info Banner */}
                  <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-orange-500/20 rounded-lg">
                        <ListChecks className="h-5 w-5 text-orange-400" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-orange-400">
                          {selectedRequest.template_info.name}
                        </h4>
                        {selectedRequest.template_info.description && (
                          <p className="text-sm text-zinc-400 mt-1">
                            {selectedRequest.template_info.description}
                          </p>
                        )}
                        <p className="text-xs text-zinc-500 mt-2">
                          {tr("Полів для заповнення")}: {selectedRequest.template_info.fields.length}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Required Data Preview */}
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-zinc-400 flex items-center gap-2">
                      <Eye className="h-4 w-4" />
                      {tr("Що потрібно від вас:")}
                    </h4>
                    <div className="space-y-2 p-3 bg-zinc-800/50 rounded-lg">
                      {selectedRequest.template_info.fields.map((field) => {
                        const Icon = FIELD_ICONS[field.field_type] || FileText;
                        return (
                          <div 
                            key={field.id} 
                            className="flex items-start gap-3 p-2 bg-zinc-800/50 rounded border border-zinc-700/50"
                          >
                            <Icon className="h-4 w-4 text-zinc-500 mt-0.5 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-zinc-50">
                                  {field.label}
                                </span>
                                {field.is_required && (
                                  <Badge className="bg-red-500/20 text-red-400 text-[10px] px-1.5">
                                    {tr("Обовʼязково")}
                                  </Badge>
                                )}
                              </div>
                              {field.help_text && (
                                <p className="text-xs text-zinc-500 mt-0.5">{field.help_text}</p>
                              )}
                              <Badge variant="outline" className="mt-1 text-[10px] bg-zinc-700/50 text-zinc-400 border-zinc-600">
                                {field.field_type === "file" ? tr("Файл") : field.field_type}
                              </Badge>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Additional Notes */}
                  {selectedRequest.description && (
                    <div className="p-3 bg-zinc-800/50 rounded-lg">
                      <p className="text-xs text-zinc-500 mb-1">{tr("Додаткові примітки:")}</p>
                      <p className="text-sm text-zinc-300">{selectedRequest.description}</p>
                    </div>
                  )}

                  {/* Project Context */}
                  {selectedRequest.project_name && (
                    <div className="flex items-center gap-2 text-xs text-zinc-500">
                      <FolderKanban className="h-3 w-3" />
                      {tr("Проєкт")}: {selectedRequest.project_name}
                      {selectedRequest.task_name && ` • ${tr("Задача")}: ${selectedRequest.task_name}`}
                    </div>
                  )}

                  {/* Start Filling Button */}
                  <div className="flex gap-2 justify-end pt-2">
                    <Button
                      variant="outline"
                      onClick={closeRequestDialog}
                      className="border-zinc-700 text-zinc-400"
                    >
                      {tr("Закрити")}
                    </Button>
                    <Button
                      onClick={() => setDialogMode("fill")}
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                    >
                      {tr("Почати заповнення")}
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </>
              )}

              {/* FILL MODE - Form to fill template fields */}
              {dialogMode === "fill" && selectedRequest.template_info && (
                <>
                  {/* Back Button */}
                  <Button
                    variant="ghost"
                    onClick={() => setDialogMode("preview")}
                    className="text-zinc-400 hover:text-zinc-50 p-0 h-auto"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    {tr("Назад до огляду")}
                  </Button>

                  {/* Template Fields Form */}
                  <div className="space-y-4 p-4 bg-zinc-800/30 rounded-lg border border-zinc-700/50">
                    <p className="text-xs text-orange-400 font-medium flex items-center gap-1">
                      <ListChecks className="h-3 w-3" />
                      {selectedRequest.template_info.name}
                    </p>
                    
                    {selectedRequest.template_info.fields.map((field) => {
                      const Icon = FIELD_ICONS[field.field_type] || FileText;
                      const value = templateFieldValues[field.id] || "";

                      return (
                        <div key={field.id} className="space-y-1.5">
                          <Label className="text-zinc-400 flex items-center gap-2">
                            <Icon className="h-3.5 w-3.5" />
                            {field.label}
                            {field.is_required && <span className="text-red-400">*</span>}
                          </Label>

                          {field.field_type === "checkbox" && (
                            <div className="flex items-center gap-2">
                              <Checkbox
                                checked={value === "true" || value === true}
                                onCheckedChange={(checked) => updateFieldValue(field.id, checked)}
                                className="border-zinc-600"
                              />
                              <span className="text-sm text-zinc-400">
                                {field.help_text || tr("Позначте, якщо застосовно")}
                              </span>
                            </div>
                          )}

                          {field.field_type === "text" && (
                            <Input
                              value={value}
                              onChange={(e) => updateFieldValue(field.id, e.target.value)}
                              placeholder={field.placeholder || `${tr("Введіть")}: ${field.label.toLowerCase()}`}
                              className="bg-zinc-800 border-zinc-700 text-zinc-50"
                            />
                          )}

                          {field.field_type === "number" && (
                            <Input
                              type="number"
                              value={value}
                              onChange={(e) => updateFieldValue(field.id, e.target.value)}
                              placeholder={field.placeholder || "0"}
                              className="bg-zinc-800 border-zinc-700 text-zinc-50"
                            />
                          )}

                          {field.field_type === "dropdown" && (
                            <Select value={value} onValueChange={(v) => updateFieldValue(field.id, v)}>
                              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                                <SelectValue placeholder={`${tr("Оберіть")}: ${field.label.toLowerCase()}`} />
                              </SelectTrigger>
                              <SelectContent className="bg-zinc-800 border-zinc-700">
                                {field.options?.map((opt) => (
                                  <SelectItem key={opt.value || opt} value={opt.value || opt}>
                                    {opt.label || opt}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}

                          {field.field_type === "textarea" && (
                            <Textarea
                              value={value}
                              onChange={(e) => updateFieldValue(field.id, e.target.value)}
                              placeholder={field.placeholder || `${tr("Введіть")}: ${field.label.toLowerCase()}`}
                              className="bg-zinc-800 border-zinc-700 text-zinc-50 min-h-[60px]"
                            />
                          )}

                          {field.field_type === "date" && (
                            <Input
                              type="date"
                              value={value}
                              onChange={(e) => updateFieldValue(field.id, e.target.value)}
                              className="bg-zinc-800 border-zinc-700 text-zinc-50"
                            />
                          )}

                          {field.field_type === "url" && (
                            <Input
                              type="url"
                              value={value}
                              onChange={(e) => updateFieldValue(field.id, e.target.value)}
                              placeholder={field.placeholder || "https://..."}
                              className="bg-zinc-800 border-zinc-700 text-zinc-50"
                            />
                          )}

                          {field.field_type === "email" && (
                            <Input
                              type="email"
                              value={value}
                              onChange={(e) => updateFieldValue(field.id, e.target.value)}
                              placeholder={field.placeholder || "email@example.com"}
                              className="bg-zinc-800 border-zinc-700 text-zinc-50"
                            />
                          )}

                          {field.field_type === "file" && (
                            <FileUploadField
                              value={value}
                              onChange={(url) => updateFieldValue(field.id, url)}
                              placeholder={tr("Завантажте файл або вставте посилання...")}
                            />
                          )}

                          {field.help_text && field.field_type !== "checkbox" && (
                            <p className="text-xs text-zinc-500">{field.help_text}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Additional Comment */}
                  <div>
                    <Label className="text-zinc-400 mb-2 block">
                      {tr("Додатковий коментар (необовʼязково)")}
                    </Label>
                    <Textarea
                      value={responseComment}
                      onChange={(e) => setResponseComment(e.target.value)}
                      placeholder={tr("Додайте примітки за потреби...")}
                      className="bg-zinc-800 border-zinc-700 text-zinc-50 min-h-[60px]"
                    />
                  </div>

                  {/* Submit Button */}
                  <div className="flex gap-2 justify-end">
                    <Button
                      variant="outline"
                      onClick={() => setDialogMode("preview")}
                      className="border-zinc-700 text-zinc-400"
                    >
                      <ArrowLeft className="h-4 w-4 mr-1" />
                      {tr("Назад")}
                    </Button>
                    <Button
                      onClick={() => handleRespond(selectedRequest.id, "info_provided")}
                      disabled={submitting || !validateRequiredFields()}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {submitting ? (
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4 mr-2" />
                      )}
                      {tr("Надіслати відповідь")}
                    </Button>
                  </div>
                </>
              )}

              {/* NO TEMPLATE - Original Simple Response */}
              {!selectedRequest.template_info && (
                <>
                  {/* Request Details */}
                  <div className="p-3 bg-zinc-800/50 rounded-lg">
                    <p className="text-sm text-zinc-300">
                      {selectedRequest.description || tr("Додаткових деталей немає.")}
                    </p>
                    {selectedRequest.project_name && (
                      <p className="text-xs text-zinc-500 mt-2">
                        {tr("Проєкт")}: {selectedRequest.project_name}
                        {selectedRequest.task_name && ` • ${tr("Задача")}: ${selectedRequest.task_name}`}
                      </p>
                    )}
                  </div>

                  {/* Comment Input */}
                  <div>
                    <label className="text-sm text-zinc-400 mb-2 block">
                      {tr("Ваш коментар (необовʼязково)")}
                    </label>
                    <Textarea
                      value={responseComment}
                      onChange={(e) => setResponseComment(e.target.value)}
                      placeholder={tr("Додайте відгук або примітки...")}
                      className="bg-zinc-800 border-zinc-700 text-zinc-50 min-h-[100px]"
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 justify-end">
                    {selectedRequest.request_type === "work_review" && (
                      <>
                        <Button
                          variant="outline"
                          onClick={() => handleRespond(selectedRequest.id, "revision")}
                          disabled={submitting}
                          className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                        >
                          <RefreshCw className="h-4 w-4 mr-2" />
                          {tr("Потрібні правки")}
                        </Button>
                        <Button
                          onClick={() => handleRespond(selectedRequest.id, "approved")}
                          disabled={submitting}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle2 className="h-4 w-4 mr-2" />
                          {tr("Погодити роботу")}
                        </Button>
                      </>
                    )}
                    {selectedRequest.request_type === "approval_request" && (
                      <>
                        <Button
                          variant="outline"
                          onClick={() => handleRespond(selectedRequest.id, "revision")}
                          disabled={submitting}
                          className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                        >
                          {tr("Потрібні зміни")}
                        </Button>
                        <Button
                          onClick={() => handleRespond(selectedRequest.id, "approved")}
                          disabled={submitting}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle2 className="h-4 w-4 mr-2" />
                          {tr("Погодити")}
                        </Button>
                      </>
                    )}
                    {selectedRequest.request_type === "info_request" && (
                      <Button
                        onClick={() => handleRespond(selectedRequest.id, "info_provided")}
                        disabled={submitting || !responseComment.trim()}
                        className="bg-orange-500 hover:bg-orange-600 text-white"
                      >
                        <Send className="h-4 w-4 mr-2" />
                        {tr("Надіслати інформацію")}
                      </Button>
                    )}
                    {selectedRequest.request_type === "scope_approval" && (
                      <>
                        <Button
                          variant="outline"
                          onClick={() => handleRespond(selectedRequest.id, "rejected")}
                          disabled={submitting}
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                        >
                          <AlertTriangle className="h-4 w-4 mr-2" />
                          {tr("Відхилити зміни")}
                        </Button>
                        <Button
                          onClick={() => handleRespond(selectedRequest.id, "approved")}
                          disabled={submitting}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle2 className="h-4 w-4 mr-2" />
                          {tr("Погодити зміни")}
                        </Button>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Task Review Dialog */}
      <Dialog open={!!selectedTask} onOpenChange={() => { setSelectedTask(null); setTaskReviewFeedback(""); }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[85vh] overflow-y-auto">
          {selectedTask && (
            <>
              <DialogHeader>
                <DialogTitle className="text-zinc-50 flex items-center gap-2">
                  <FileText className="h-5 w-5 text-sky-400" />
                  {selectedTask.name}
                </DialogTitle>
                <div className="flex items-center gap-2 mt-1">
                  <TaskStageBadge task={selectedTask} size="sm" />
                  {selectedTask.task_type && (
                    <Badge variant="outline" className="text-xs bg-zinc-700/50 text-zinc-400">
                      {selectedTask.task_type}
                    </Badge>
                  )}
                </div>
              </DialogHeader>

              <div className="space-y-4 py-2">
                {/* Task Description */}
                {(selectedTask.description || selectedTask.brief) && (
                  <div className="p-3 bg-zinc-800/50 rounded-lg">
                    <p className="text-sm text-zinc-300">
                      {selectedTask.description || selectedTask.brief}
                    </p>
                  </div>
                )}

                {/* Versions / Deliverables */}
                {selectedTask.versions && selectedTask.versions.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-zinc-400 flex items-center gap-2">
                      <Eye className="h-4 w-4" />
                      {tr("Результати")} ({selectedTask.versions.length} {tr("верс.")})
                    </h4>
                    <div className="space-y-2">
                      {selectedTask.versions.map((version, idx) => (
                        <div key={version.id || idx} className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700/50">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-zinc-50">
                              {tr("Версія")} {version.version_number || idx + 1}
                            </span>
                            {version.status && (
                              <Badge variant="outline" className={cn("text-xs", 
                                version.status === "approved" ? "bg-green-500/10 text-green-400" :
                                version.status === "revision_requested" ? "bg-orange-500/10 text-orange-400" :
                                "bg-zinc-500/10 text-zinc-400"
                              )}>
                                {version.status}
                              </Badge>
                            )}
                          </div>
                          
                          {/* Files in version */}
                          {version.files && version.files.length > 0 && (
                            <div className="space-y-1">
                              {version.files.map((file, fileIdx) => (
                                <a
                                  key={fileIdx}
                                  href={file.url || file}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-2 p-2 bg-zinc-700/50 rounded hover:bg-zinc-700 transition-colors"
                                >
                                  <Download className="h-4 w-4 text-sky-400" />
                                  <span className="text-sm text-zinc-300 flex-1">
                                    {file.name || file.filename || `File ${fileIdx + 1}`}
                                  </span>
                                  <span className="text-xs text-zinc-500">
                                    {file.size ? `${(file.size / 1024 / 1024).toFixed(1)} MB` : ''}
                                  </span>
                                </a>
                              ))}
                            </div>
                          )}
                          
                          {version.client_feedback && (
                            <div className="mt-2 p-2 bg-zinc-900/50 rounded text-xs text-zinc-400">
                              <span className="text-zinc-500">{tr("Ваш відгук: ")}</span>
                              {version.client_feedback}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Reference Files */}
                {selectedTask.files && selectedTask.files.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-zinc-400">{tr("Референсні файли")}</h4>
                    <div className="grid gap-1">
                      {selectedTask.files.map((file, idx) => (
                        <a
                          key={idx}
                          href={file.url || file}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 p-2 bg-zinc-800/50 rounded hover:bg-zinc-800"
                        >
                          <FileText className="h-4 w-4 text-zinc-400" />
                          <span className="text-sm text-zinc-300">{file.name || `File ${idx + 1}`}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Review Section - only show for tasks in review status */}
                {["review", "in_progress", "assigned", "delivered"].includes(selectedTask.status) && (
                  <div className="space-y-3 pt-2 border-t border-zinc-800">
                    <h4 className="text-sm font-medium text-zinc-400">{tr("Ваш відгук")}</h4>
                    <Textarea
                      value={taskReviewFeedback}
                      onChange={(e) => setTaskReviewFeedback(e.target.value)}
                      placeholder={tr("Додайте відгук (для погодження — необовʼязково, для правок — бажано)...")}
                      className="bg-zinc-800 border-zinc-700 text-zinc-50 min-h-[80px]"
                    />
                    <div className="flex gap-2 justify-end">
                      <Button
                        onClick={() => handleTaskReview(selectedTask.id, "revision")}
                        disabled={reviewingTask}
                        variant="outline"
                        className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        {tr("Потрібні правки")}
                      </Button>
                      <Button
                        onClick={() => handleTaskReview(selectedTask.id, "approve")}
                        disabled={reviewingTask}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {reviewingTask ? (
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <CheckCircle2 className="h-4 w-4 mr-2" />
                        )}
                        {tr("Погодити роботу")}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Already completed message */}
                {selectedTask.status === "completed" && (
                  <div className="p-3 bg-green-900/20 border border-green-500/30 rounded-lg text-center">
                    <CheckCircle2 className="h-6 w-6 text-green-400 mx-auto mb-1" />
                    <p className="text-sm text-green-400">{tr("Цю задачу завершено")}</p>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* View History Request Dialog */}
      <Dialog open={!!viewHistoryRequest} onOpenChange={() => setViewHistoryRequest(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <Eye className="h-5 w-5 text-sky-400" />
              {tr("Ваша надіслана відповідь")}
            </DialogTitle>
          </DialogHeader>
          
          {viewHistoryLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin h-6 w-6 border-2 border-orange-500 border-t-transparent rounded-full" />
            </div>
          ) : viewHistoryRequest && (
            <div className="space-y-4 py-2">
              {/* Title */}
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-zinc-50">{viewHistoryRequest.title}</h3>
                <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/30">
                  ✓ {tr("Надіслано")}: {viewHistoryRequest.response_type}
                </Badge>
              </div>

              {/* Info */}
              <div className="grid grid-cols-2 gap-3 p-3 bg-zinc-800/50 rounded-lg text-sm">
                {viewHistoryRequest.project_name && (
                  <div>
                    <p className="text-xs text-zinc-500">{tr("Проєкт")}</p>
                    <p className="text-zinc-50">{viewHistoryRequest.project_name}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-zinc-500">{tr("Дата відповіді")}</p>
                  <p className="text-zinc-50">
                    {viewHistoryRequest.responded_at 
                      ? new Date(viewHistoryRequest.responded_at).toLocaleDateString()
                      : "—"}
                  </p>
                </div>
              </div>

              {/* Description */}
              {viewHistoryRequest.description && (
                <div className="space-y-1">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">{tr("Опис запиту")}</p>
                  <p className="text-sm text-zinc-300 whitespace-pre-wrap">{viewHistoryRequest.description}</p>
                </div>
              )}

              {/* Your Comment */}
              {viewHistoryRequest.response_comment && (
                <div className="space-y-1 p-3 bg-sky-500/10 border border-sky-500/20 rounded-lg">
                  <p className="text-xs text-sky-400 uppercase tracking-wider">{tr("Ваш коментар")}</p>
                  <p className="text-sm text-zinc-300 whitespace-pre-wrap">{viewHistoryRequest.response_comment}</p>
                </div>
              )}

              {/* Template Fields with Your Submitted Data */}
              {viewHistoryRequest.template_fields && viewHistoryRequest.template_fields.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">{tr("Ваші надіслані дані")}</p>
                  <div className="space-y-2 p-3 bg-zinc-800/30 rounded-lg border border-zinc-800">
                    {viewHistoryRequest.template_fields.map((field) => {
                      const Icon = FIELD_ICONS[field.field_type] || FileText;
                      const value = viewHistoryRequest.template_data?.[field.id];
                      const hasValue = value && value !== "";
                      
                      return (
                        <div key={field.id} className="py-2 px-2 bg-zinc-800/50 rounded">
                          <div className="flex items-center gap-2 mb-1">
                            <Icon className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
                            <span className="text-sm text-zinc-400 font-medium">{field.label}</span>
                          </div>
                          {hasValue ? (
                            <div className="mt-1 ml-5">
                              {field.field_type === "file" ? (
                                <a 
                                  href={value} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-sky-400 hover:text-sky-300 text-sm flex items-center gap-1"
                                >
                                  <Download className="h-3 w-3" />
                                  {tr("Завантажити ваш файл")}
                                </a>
                              ) : field.field_type === "url" ? (
                                <a 
                                  href={value.startsWith("http") ? value : `https://${value}`} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-sky-400 hover:text-sky-300 text-sm flex items-center gap-1 break-all"
                                >
                                  <LinkIcon className="h-3 w-3 shrink-0" />
                                  {value}
                                </a>
                              ) : field.field_type === "checkbox" ? (
                                <span className={cn("text-sm", value ? "text-green-400" : "text-zinc-500")}>
                                  {value ? tr("✓ Так") : tr("✗ Ні")}
                                </span>
                              ) : (
                                <p className="text-zinc-50 text-sm whitespace-pre-wrap">{value}</p>
                              )}
                            </div>
                          ) : (
                            <p className="text-zinc-600 text-sm italic mt-1 ml-5">{tr("Не вказано")}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setViewHistoryRequest(null)} className="border-zinc-700">
              {tr("Закрити")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Task Change Request Approval Dialog */}
      <Dialog 
        open={!!selectedChangeRequest} 
        onOpenChange={() => { setSelectedChangeRequest(null); setChangeRequestComment(""); }}
      >
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-400" />
              {tr("Запит на зміни")}
            </DialogTitle>
          </DialogHeader>
          
          {selectedChangeRequest && (
            <div className="space-y-4 py-2">
              {/* Task & Project info */}
              <div className="p-3 bg-zinc-800/50 rounded-lg">
                <p className="text-sm font-medium text-zinc-50">{selectedChangeRequest.task_name}</p>
                <p className="text-xs text-zinc-500">{selectedChangeRequest.project_name}</p>
              </div>
              
              {/* Change type badge */}
              <Badge variant="outline" className="text-sm bg-amber-500/20 text-amber-400 border-amber-500/30">
                {getChangeTypeLabel(selectedChangeRequest.change_type)}
              </Badge>
              
              {/* Change details */}
              <div className="p-4 bg-zinc-800/30 rounded-lg border border-zinc-700/50 space-y-3">
                {selectedChangeRequest.change_type === "budget_change" && (
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-zinc-400">{tr("Поточний бюджет:")}</span>
                      <span className="text-sm text-red-400 line-through">
                        {selectedChangeRequest.old_client_price?.toLocaleString()} {selectedChangeRequest.currency || "$"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-zinc-400">{tr("Новий бюджет:")}</span>
                      <span className="text-sm text-green-400 font-semibold">
                        {selectedChangeRequest.new_client_price?.toLocaleString()} {selectedChangeRequest.currency || "$"}
                      </span>
                    </div>
                    {selectedChangeRequest.old_client_price && selectedChangeRequest.new_client_price && (
                      <div className="flex justify-between items-center pt-2 border-t border-zinc-700">
                        <span className="text-sm text-zinc-400">{tr("Різниця:")}</span>
                        <span className={cn(
                          "text-sm font-semibold",
                          selectedChangeRequest.new_client_price > selectedChangeRequest.old_client_price ? "text-red-400" : "text-green-400"
                        )}>
                          {selectedChangeRequest.new_client_price > selectedChangeRequest.old_client_price ? "+" : ""}
                          {(selectedChangeRequest.new_client_price - selectedChangeRequest.old_client_price).toLocaleString()} {selectedChangeRequest.currency || "$"}
                        </span>
                      </div>
                    )}
                  </div>
                )}
                
                {selectedChangeRequest.change_type === "deadline_change" && (
                  <div className="space-y-2">
                    {selectedChangeRequest.old_deadline && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-zinc-400">{tr("Поточний дедлайн:")}</span>
                        <span className="text-sm text-red-400 line-through">
                          {new Date(selectedChangeRequest.old_deadline).toLocaleDateString('uk-UA')}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-zinc-400">{tr("Новий дедлайн:")}</span>
                      <span className="text-sm text-green-400 font-semibold">
                        {selectedChangeRequest.new_deadline 
                          ? new Date(selectedChangeRequest.new_deadline).toLocaleDateString('uk-UA')
                          : tr("Не вказано")}
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Scope change includes both budget and deadline */}
                {selectedChangeRequest.change_type === "scope_change" && (
                  <div className="space-y-3">
                    {/* Budget changes if present */}
                    {(selectedChangeRequest.old_client_price !== undefined || selectedChangeRequest.new_client_price !== undefined) && (
                      <div className="space-y-2">
                        <p className="text-xs text-zinc-500 font-medium">{tr("Бюджет:")}</p>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-zinc-400">{tr("Поточний:")}</span>
                          <span className="text-sm text-red-400 line-through">
                            {selectedChangeRequest.old_client_price?.toLocaleString() || selectedChangeRequest.original_client_price?.toLocaleString()} {selectedChangeRequest.currency || "$"}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-zinc-400">{tr("Новий:")}</span>
                          <span className="text-sm text-green-400 font-semibold">
                            {selectedChangeRequest.new_client_price?.toLocaleString()} {selectedChangeRequest.currency || "$"}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {/* Deadline changes if present */}
                    {selectedChangeRequest.new_deadline && (
                      <div className="space-y-2 pt-2 border-t border-zinc-700">
                        <p className="text-xs text-zinc-500 font-medium">{tr("Дедлайн:")}</p>
                        {(selectedChangeRequest.old_deadline || selectedChangeRequest.original_deadline) && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-zinc-400">{tr("Поточний:")}</span>
                            <span className="text-sm text-red-400 line-through">
                              {new Date(selectedChangeRequest.old_deadline || selectedChangeRequest.original_deadline).toLocaleDateString('uk-UA')}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-zinc-400">{tr("Новий:")}</span>
                          <span className="text-sm text-green-400 font-semibold">
                            {new Date(selectedChangeRequest.new_deadline).toLocaleDateString('uk-UA')}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Reason */}
              {selectedChangeRequest.reason && (
                <div className="p-3 bg-zinc-800/50 rounded-lg">
                  <p className="text-xs text-zinc-500 mb-1">{tr("Причина зміни:")}</p>
                  <p className="text-sm text-zinc-300">{selectedChangeRequest.reason}</p>
                </div>
              )}
              
              {/* Comment input */}
              <div>
                <Label className="text-zinc-400 mb-2 block text-sm">
                  {tr("Ваш коментар (необов'язково)")}
                </Label>
                <Textarea
                  value={changeRequestComment}
                  onChange={(e) => setChangeRequestComment(e.target.value)}
                  placeholder={tr("Додайте коментар до вашого рішення...")}
                  className="bg-zinc-800 border-zinc-700 text-zinc-50 min-h-[60px]"
                />
              </div>
              
              {/* Action buttons */}
              <div className="flex gap-2 justify-end pt-2 border-t border-zinc-800">
                <Button
                  variant="outline"
                  onClick={() => handleChangeRequestResponse(selectedChangeRequest.id, "reject")}
                  disabled={submittingChangeRequest}
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                >
                  {submittingChangeRequest ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 mr-2" />
                  )}
                  {tr("Відхилити")}
                </Button>
                <Button
                  onClick={() => handleChangeRequestResponse(selectedChangeRequest.id, "approve")}
                  disabled={submittingChangeRequest}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {submittingChangeRequest ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                  )}
                  {tr("Схвалити")}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientPortalPage;
